/**
 * ClosingFlow — Main closing checklist management UI.
 * Shows grouped checklist items with progress tracking, status toggling,
 * overdue warnings, and collapsible category sections.
 *
 * Ownership (QA 2026-10-19 N1): each item from the API carries `permissions`
 * (`can_update`, `can_mark_na`, `needs_both_parties`, `locked_reason`), worked
 * out by the server (backend app/services/closing_checklist.py). Controls the
 * viewer may not use are disabled with legal's tooltip; the server enforces the
 * same rules with a 403. A title-company / lender item needs both parties:
 * "→ Confirm complete" records the viewer's confirmation, and "Withdraw
 * confirmation" takes it back. Once both have confirmed (or the closing agent
 * completed it), a party sees no status controls on it: only the closing
 * agent can reopen it (PR #240 review).
 *
 * @param {Object} props
 * @param {number} props.transactionId - Active transaction ID
 * @param {number} props.buyerId - Current buyer ID
 * @returns {JSX.Element}
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useClosingChecklist, useClosingProgress, updateClosingItem } from '../../agents/closingAgent';
import './ClosingFlow.css';

const STATUS_ORDER = ['pending', 'in_progress', 'completed'];

/** `item_type` of the seller's cure commitment (backend notices.CHECKLIST_ITEM_SELLER_CURE). */
const SELLER_CURE_ITEM_TYPE = 'seller_cure';

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  not_applicable: 'N/A',
};

/** Rows from an older API carry no `permissions`: leave them editable (the server still decides). */
const DEFAULT_PERMISSIONS = {
  can_update: true,
  can_mark_na: true,
  needs_both_parties: false,
  locked_reason: null,
};

/**
 * Tooltip for each `locked_reason` (legal review 2026-10-19, copy in
 * docs/legal/general/notification-and-advisor-copy.v1.md Part 4).
 */
export const LOCK_TOOLTIPS = {
  other_party_item: 'Only the other party can mark this item complete.',
  awaiting_other_party:
    'You confirmed this item. It will be marked complete once the other party confirms it too.',
  closing_agent_only: 'Only the closing agent can mark this item as not applicable.',
  completed_agent_reopens: 'Only the closing agent can reopen this completed item.',
  confirmations_unavailable: "Confirmations for this item can't be recorded yet. Please try again later.",
};

/** Legal's checklist note (same source as LOCK_TOOLTIPS). */
export const CHECKLIST_LEGAL_NOTE =
  "This checklist tracks what was recorded on cutthe6.com. It doesn't decide whether your sale has legally closed or whether ownership has transferred.";

const permissionsOf = (item) => ({ ...DEFAULT_PERMISSIONS, ...(item.permissions || {}) });

const nextStatusOf = (status) =>
  STATUS_ORDER[(STATUS_ORDER.indexOf(status) + 1) % STATUS_ORDER.length];

/** Visible label of an item's advance button. */
const advanceLabel = (item, perms) =>
  perms.needs_both_parties && nextStatusOf(item.status) === 'completed'
    ? 'Confirm complete'
    : STATUS_LABELS[nextStatusOf(item.status)];

const GROUP_LABELS = {
  buyer: 'Buyer',
  seller: 'Seller',
  title_company: 'Title Company',
  lender: 'Lender',
};

const GROUP_ICONS = {
  buyer: '🏠',
  seller: '🔑',
  title_company: '📋',
  lender: '🏦',
};

const ClosingFlow = ({ transactionId, buyerId }) => {
  const { items, loading, error, reload } = useClosingChecklist(transactionId);
  useClosingProgress(transactionId);

  const [expandedItem, setExpandedItem] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [updating, setUpdating] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Group items by assigned_to
  const grouped = useMemo(() => {
    const groups = {};
    (items || []).forEach((item) => {
      const key = item.assigned_to || 'buyer';
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [items]);

  // Overdue items
  const overdueItems = useMemo(() => {
    const now = new Date();
    return (items || []).filter(
      (item) =>
        item.due_date &&
        new Date(item.due_date) < now &&
        item.status !== 'completed' &&
        item.status !== 'not_applicable'
    );
  }, [items]);

  // Progress stats (overall)
  const completed = useMemo(
    () => (items || []).filter((i) => i.status === 'completed').length,
    [items]
  );
  const total = (items || []).length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Per-group progress
  const groupProgress = useMemo(() => {
    const out = {};
    Object.entries(grouped).forEach(([group, groupItems]) => {
      const done = groupItems.filter((i) => i.status === 'completed').length;
      const na   = groupItems.filter((i) => i.status === 'not_applicable').length;
      const effective = groupItems.length - na;
      out[group] = { done, total: groupItems.length, effective, pct: effective > 0 ? Math.round((done / effective) * 100) : 0 };
    });
    return out;
  }, [grouped]);

  // Dynamic ring color — uses CSS custom property token names from App.css
  const ringColor = pct >= 66 ? 'var(--color-success)' : pct >= 33 ? 'var(--color-warning)' : 'var(--color-accent)';

  const sendStatus = useCallback(
    async (item, status) => {
      setUpdating(item.id);
      setActionError(null);
      try {
        await updateClosingItem(item.id, { status });
        reload();
      } catch (err) {
        console.error('Failed to update closing item:', err);
        setActionError(err?.message || 'Could not update this item.');
      } finally {
        setUpdating(null);
      }
    },
    [reload]
  );

  // Cycle status: pending → in_progress → completed
  const handleStatusCycle = useCallback(
    (item) => sendStatus(item, nextStatusOf(item.status)),
    [sendStatus]
  );

  // Reopen a completed item. Deliberately not part of the "→ Next" cycle: that
  // wrapped completed → pending in one click, so a stray click un-completed a
  // closing item (QA run 8 R8-15).
  const handleReopen = useCallback(
    (item) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Reopen "${item.title}"? It will no longer count as complete.`)) return;
      sendStatus(item, 'in_progress');
    },
    [sendStatus]
  );

  // Take back the viewer's confirmation of a two-party item.
  const handleWithdraw = useCallback(
    (item) => sendStatus(item, 'in_progress'),
    [sendStatus]
  );

  // Mark N/A
  const handleMarkNA = useCallback(
    (item) => sendStatus(item, 'not_applicable'),
    [sendStatus]
  );

  const toggleGroup = (group) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const toggleExpand = (id) => {
    setExpandedItem((prev) => (prev === id ? null : id));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // ---------- Render ----------

  if (loading && !items.length) {
    return (
      <div className="closing-flow">
        <div className="closing-loading">
          <div className="closing-loading-spinner" />
          <p>Loading closing checklist…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="closing-flow">
        <div className="closing-error">
          <span className="closing-error-icon">⚠</span>
          <p>{error}</p>
          <button className="closing-retry-btn" onClick={reload}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="closing-flow">
        <div className="closing-empty">
          <span className="closing-empty-icon">📦</span>
          <h3>No closing items yet</h3>
          <p>
            Initialize your transaction journey to see your closing checklist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="closing-flow">
      {/* Overdue banner */}
      {overdueItems.length > 0 && (
        <div className="closing-overdue-banner">
          <span className="closing-overdue-icon" aria-hidden="true">🚨</span>
          <span role="status" aria-live="polite">
            <strong>{overdueItems.length}</strong> item
            {overdueItems.length > 1 ? 's are' : ' is'} past due — review
            immediately.
          </span>
        </div>
      )}

      {/* Progress header */}
      <div className="closing-progress-header">
        <div className="closing-progress-text">
          <h2>Closing Checklist</h2>
          <p className="closing-progress-sub">
            {completed} of {total} items complete
          </p>
          {pct === 100 && (
            <p className="closing-all-done">🎉 All items complete — clear to close!</p>
          )}
          <p className="closing-legal-note" data-testid="closing-legal-note">
            {CHECKLIST_LEGAL_NOTE}
          </p>
        </div>
        <div className="closing-progress-ring" role="img" aria-label={`${pct}% complete`}>
          <svg viewBox="0 0 80 80" className="closing-ring-svg">
            <circle cx="40" cy="40" r="34" className="closing-ring-track" />
            <circle
              cx="40"
              cy="40"
              r="34"
              className="closing-ring-fill"
              style={{ stroke: ringColor }}
              strokeDasharray={`${(pct / 100) * 213.6} 213.6`}
            />
          </svg>
          <span className="closing-ring-label" style={{ color: ringColor }}>{pct}%</span>
        </div>
      </div>

      {actionError && (
        <div className="closing-action-error" role="alert">
          {actionError}
        </div>
      )}

      {/* Grouped checklist */}
      <div className="closing-groups">
        {Object.entries(grouped).map(([group, groupItems]) => (
          <div className="closing-group" key={group}>
            <button
              className="closing-group-header"
              onClick={() => toggleGroup(group)}
              aria-expanded={!collapsedGroups[group]}
            >
              <span className="closing-group-icon" aria-hidden="true">
                {GROUP_ICONS[group] || '📌'}
              </span>
              <span className="closing-group-label">
                {GROUP_LABELS[group] || group}
              </span>
              <span
                className={`closing-group-done-badge ${
                  groupProgress[group]?.pct === 100 ? 'closing-group-done-complete' : ''
                }`}
              >
                {groupProgress[group]?.done ?? 0}/{groupProgress[group]?.effective ?? groupItems.length}
              </span>
              <span
                className={`closing-chevron ${
                  collapsedGroups[group] ? 'closing-chevron-collapsed' : ''
                }`}
              >
                ▾
              </span>
            </button>

            {/* Mini group progress bar */}
            <div
              className="closing-group-mini-bar"
              aria-hidden="true"
              style={{ width: `${groupProgress[group]?.pct ?? 0}%`, opacity: collapsedGroups[group] ? 0 : 1 }}
            />

            {!collapsedGroups[group] && (
              <div className="closing-group-items">
                {groupItems.map((item) => {
                  const perms = permissionsOf(item);
                  const awaiting = perms.locked_reason === 'awaiting_other_party';
                  // A completed two-party item: no advance / N/A controls at all.
                  const agentReopens = perms.locked_reason === 'completed_agent_reopens';
                  const canAdvance =
                    perms.can_update && !awaiting && perms.locked_reason !== 'confirmations_unavailable';
                  const lockTip = LOCK_TOOLTIPS[perms.locked_reason] || null;
                  const naTip = perms.can_mark_na
                    ? null
                    : LOCK_TOOLTIPS[perms.can_update ? 'closing_agent_only' : 'other_party_item'];
                  const label = advanceLabel(item, perms);
                  const isCompleted = item.status === 'completed';
                  return (
                  <div
                    className={`closing-item ${
                      expandedItem === item.id ? 'closing-item-expanded' : ''
                    } ${updating === item.id ? 'closing-item-updating' : ''}`}
                    key={item.id}
                  >
                    <div className="closing-item-row">
                      <button
                        type="button"
                        className="closing-item-toggle"
                        onClick={() => toggleExpand(item.id)}
                        aria-expanded={expandedItem === item.id}
                        aria-describedby={
                          item.item_type === SELLER_CURE_ITEM_TYPE && item.description
                            ? `closing-item-${item.id}-description`
                            : undefined
                        }
                      >
                        <span className="closing-item-title">{item.title}</span>
                        <span
                          className={`closing-status-badge closing-status-${item.status}`}
                        >
                          {STATUS_LABELS[item.status] || item.status}
                        </span>
                        {item.due_date && (
                          <span className="closing-item-due">
                            {formatDate(item.due_date)}
                          </span>
                        )}
                      </button>
                      {/* Inline quick-cycle — sibling of toggle, not nested. A disabled
                          button fires no hover events, so the tooltip sits on a wrapper. */}
                      {item.status !== 'not_applicable' && !agentReopens && !isCompleted && (
                        <span className="closing-control-wrap" title={canAdvance ? undefined : lockTip || undefined}>
                          <button
                            className="closing-inline-cycle"
                            title={canAdvance ? `Advance to: ${label}` : undefined}
                            onClick={() => handleStatusCycle(item)}
                            disabled={updating === item.id || !canAdvance}
                            aria-label={canAdvance ? `Advance status to ${label}` : `${item.title}: ${lockTip || 'locked'}`}
                          >
                            {updating === item.id ? '…' : canAdvance ? '→ Next' : '🔒'}
                          </button>
                        </span>
                      )}
                    </div>

                    {perms.needs_both_parties && (
                      <p className="closing-item-confirmations" data-testid={`closing-item-${item.id}-confirmations`}>
                        Buyer {item.buyer_confirmed ? 'confirmed' : 'not confirmed'} · Seller{' '}
                        {item.seller_confirmed ? 'confirmed' : 'not confirmed'}
                      </p>
                    )}

                    {/* WI seller cure (T9, copy §6.2): the server renders the title and
                        description from legal's copy; the description is always shown
                        because it says what marking the item done does and doesn't mean. */}
                    {item.item_type === SELLER_CURE_ITEM_TYPE && item.description && (
                      <p
                        className="closing-item-description"
                        id={`closing-item-${item.id}-description`}
                        data-testid="closing-item-seller-cure-description"
                      >
                        {item.description}
                      </p>
                    )}

                    {expandedItem === item.id && (
                      <div className="closing-item-details">
                        {item.notes && (
                          <p className="closing-item-notes">{item.notes}</p>
                        )}
                        {item.linked_document && (
                          <a
                            className="closing-item-doc-link"
                            href={item.linked_document}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span aria-hidden="true">📄</span> View Document
                          </a>
                        )}
                        <div className="closing-item-actions">
                          {!agentReopens && !isCompleted && (
                            <span className="closing-control-wrap" title={canAdvance ? undefined : lockTip || undefined}>
                              <button
                                className="closing-cycle-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusCycle(item);
                                }}
                                disabled={updating === item.id || !canAdvance}
                              >
                                {updating === item.id ? 'Updating…' : `→ ${label}`}
                              </button>
                            </span>
                          )}
                          {!agentReopens && isCompleted && canAdvance && (
                            <button
                              className="closing-cycle-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReopen(item);
                              }}
                              disabled={updating === item.id}
                            >
                              {updating === item.id ? 'Updating…' : '↺ Reopen'}
                            </button>
                          )}
                          {awaiting && (
                            <button
                              className="closing-na-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWithdraw(item);
                              }}
                              disabled={updating === item.id}
                            >
                              Withdraw confirmation
                            </button>
                          )}
                          {item.status !== 'not_applicable' && !agentReopens && (
                            <span className="closing-control-wrap" title={naTip || undefined}>
                              <button
                                className="closing-na-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkNA(item);
                                }}
                                disabled={updating === item.id || !perms.can_mark_na}
                              >
                                Mark N/A
                              </button>
                            </span>
                          )}
                        </div>
                        {lockTip && (
                          <p className="closing-item-lock-note" data-testid={`closing-item-${item.id}-lock`}>
                            {lockTip}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClosingFlow;
